import type { Vec3 } from './vec3'
import type { Mat4 } from './mat4'

/** Quaternion as [x, y, z, w]. */
export type Quat = [number, number, number, number]

/** Euler rotation order used everywhere in the SDK (matches three.js default). */
export type EulerOrder = 'XYZ'

const DEG2RAD = Math.PI / 180
const RAD2DEG = 180 / Math.PI

export const quat = {
  identity(): Quat {
    return [0, 0, 0, 1]
  },
  clone(q: Quat): Quat {
    return [q[0], q[1], q[2], q[3]]
  },
  copy(out: Quat, q: Quat): Quat {
    out[0] = q[0]
    out[1] = q[1]
    out[2] = q[2]
    out[3] = q[3]
    return out
  },
  set(out: Quat, x: number, y: number, z: number, w: number): Quat {
    out[0] = x
    out[1] = y
    out[2] = z
    out[3] = w
    return out
  },
  length(q: Quat): number {
    return Math.hypot(q[0], q[1], q[2], q[3])
  },
  normalize(out: Quat, q: Quat): Quat {
    const l = Math.hypot(q[0], q[1], q[2], q[3])
    if (l === 0) {
      out[0] = out[1] = out[2] = 0
      out[3] = 1
    } else {
      out[0] = q[0] / l
      out[1] = q[1] / l
      out[2] = q[2] / l
      out[3] = q[3] / l
    }
    return out
  },
  dot(a: Quat, b: Quat): number {
    return a[0] * b[0] + a[1] * b[1] + a[2] * b[2] + a[3] * b[3]
  },
  conjugate(out: Quat, q: Quat): Quat {
    out[0] = -q[0]
    out[1] = -q[1]
    out[2] = -q[2]
    out[3] = q[3]
    return out
  },
  /** out = a * b (apply b first, then a). */
  multiply(out: Quat, a: Quat, b: Quat): Quat {
    const ax = a[0], ay = a[1], az = a[2], aw = a[3]
    const bx = b[0], by = b[1], bz = b[2], bw = b[3]
    out[0] = ax * bw + aw * bx + ay * bz - az * by
    out[1] = ay * bw + aw * by + az * bx - ax * bz
    out[2] = az * bw + aw * bz + ax * by - ay * bx
    out[3] = aw * bw - ax * bx - ay * by - az * bz
    return out
  },
  /** Euler angles in degrees, intrinsic XYZ order (three.js convention). */
  fromEulerDeg(out: Quat, xDeg: number, yDeg: number, zDeg: number): Quat {
    return quat.fromEuler(out, xDeg * DEG2RAD, yDeg * DEG2RAD, zDeg * DEG2RAD)
  },
  /** Euler angles in radians, XYZ order. */
  fromEuler(out: Quat, x: number, y: number, z: number): Quat {
    const c1 = Math.cos(x / 2), c2 = Math.cos(y / 2), c3 = Math.cos(z / 2)
    const s1 = Math.sin(x / 2), s2 = Math.sin(y / 2), s3 = Math.sin(z / 2)
    out[0] = s1 * c2 * c3 + c1 * s2 * s3
    out[1] = c1 * s2 * c3 - s1 * c2 * s3
    out[2] = c1 * c2 * s3 + s1 * s2 * c3
    out[3] = c1 * c2 * c3 - s1 * s2 * s3
    return out
  },
  /** Returns Euler XYZ angles in degrees [x, y, z]. */
  toEulerDeg(q: Quat): Vec3 {
    const [x, y, z, w] = q
    // rotation matrix elements
    const m11 = 1 - 2 * (y * y + z * z)
    const m12 = 2 * (x * y - w * z)
    const m13 = 2 * (x * z + w * y)
    const m23 = 2 * (y * z - w * x)
    const m33 = 1 - 2 * (x * x + y * y)
    const ey = Math.asin(Math.max(-1, Math.min(1, m13)))
    let ex: number, ez: number
    if (Math.abs(m13) < 0.9999999) {
      ex = Math.atan2(-m23, m33)
      ez = Math.atan2(-m12, m11)
    } else {
      const m22 = 1 - 2 * (x * x + z * z)
      const m21 = 2 * (x * y + w * z)
      ex = Math.atan2(m21, m22)
      ez = 0
    }
    return [ex * RAD2DEG, ey * RAD2DEG, ez * RAD2DEG]
  },
  fromAxisAngle(out: Quat, axis: Vec3, rad: number): Quat {
    const h = rad / 2
    const s = Math.sin(h)
    out[0] = axis[0] * s
    out[1] = axis[1] * s
    out[2] = axis[2] * s
    out[3] = Math.cos(h)
    return out
  },
  /** Extract rotation from a column-major 4x4 (scale is removed). */
  fromMat4(out: Quat, m: Mat4): Quat {
    const sx = Math.hypot(m[0]!, m[1]!, m[2]!) || 1
    const sy = Math.hypot(m[4]!, m[5]!, m[6]!) || 1
    const sz = Math.hypot(m[8]!, m[9]!, m[10]!) || 1
    const m11 = m[0]! / sx, m12 = m[4]! / sy, m13 = m[8]! / sz
    const m21 = m[1]! / sx, m22 = m[5]! / sy, m23 = m[9]! / sz
    const m31 = m[2]! / sx, m32 = m[6]! / sy, m33 = m[10]! / sz
    const trace = m11 + m22 + m33
    if (trace > 0) {
      const s = 0.5 / Math.sqrt(trace + 1)
      out[3] = 0.25 / s
      out[0] = (m32 - m23) * s
      out[1] = (m13 - m31) * s
      out[2] = (m21 - m12) * s
    } else if (m11 > m22 && m11 > m33) {
      const s = 2 * Math.sqrt(1 + m11 - m22 - m33)
      out[3] = (m32 - m23) / s
      out[0] = 0.25 * s
      out[1] = (m12 + m21) / s
      out[2] = (m13 + m31) / s
    } else if (m22 > m33) {
      const s = 2 * Math.sqrt(1 + m22 - m11 - m33)
      out[3] = (m13 - m31) / s
      out[0] = (m12 + m21) / s
      out[1] = 0.25 * s
      out[2] = (m23 + m32) / s
    } else {
      const s = 2 * Math.sqrt(1 + m33 - m11 - m22)
      out[3] = (m21 - m12) / s
      out[0] = (m13 + m31) / s
      out[1] = (m23 + m32) / s
      out[2] = 0.25 * s
    }
    return out
  },
  /** Spherical linear interpolation (shortest arc). */
  slerp(out: Quat, a: Quat, b: Quat, t: number): Quat {
    let bx = b[0], by = b[1], bz = b[2], bw = b[3]
    let cosom = a[0] * bx + a[1] * by + a[2] * bz + a[3] * bw
    if (cosom < 0) {
      cosom = -cosom
      bx = -bx
      by = -by
      bz = -bz
      bw = -bw
    }
    let scale0: number, scale1: number
    if (1 - cosom > 1e-6) {
      const omega = Math.acos(cosom)
      const sinom = Math.sin(omega)
      scale0 = Math.sin((1 - t) * omega) / sinom
      scale1 = Math.sin(t * omega) / sinom
    } else {
      scale0 = 1 - t
      scale1 = t
    }
    out[0] = scale0 * a[0] + scale1 * bx
    out[1] = scale0 * a[1] + scale1 * by
    out[2] = scale0 * a[2] + scale1 * bz
    out[3] = scale0 * a[3] + scale1 * bw
    return out
  },
  /** Angle in radians between two unit quaternions (shortest arc). */
  angle(a: Quat, b: Quat): number {
    const d = Math.min(1, Math.abs(quat.dot(a, b)))
    return 2 * Math.acos(d)
  },
  /** Rotate a vector by a unit quaternion. */
  rotateVec3(out: Vec3, q: Quat, v: Vec3): Vec3 {
    const qx = q[0], qy = q[1], qz = q[2], qw = q[3]
    const vx = v[0], vy = v[1], vz = v[2]
    // t = 2 * cross(q.xyz, v)
    const tx = 2 * (qy * vz - qz * vy)
    const ty = 2 * (qz * vx - qx * vz)
    const tz = 2 * (qx * vy - qy * vx)
    // v + w*t + cross(q.xyz, t)
    out[0] = vx + qw * tx + (qy * tz - qz * ty)
    out[1] = vy + qw * ty + (qz * tx - qx * tz)
    out[2] = vz + qw * tz + (qx * ty - qy * tx)
    return out
  },
  equals(a: Quat, b: Quat, eps = 1e-6): boolean {
    return Math.abs(Math.abs(quat.dot(a, b)) - 1) <= eps
  },
}
